import React, { useState } from "react";
import "../../App.css";

const FileUploadSection = ({
  // Required props
  onFileChange,

  // Optional props with defaults
  currentFileUrl = null,
  currentFileName = null,
  isSubmitting = false,
  disabled = false,
  accept = ".pdf,.png,.jpg,.jpeg",
  additionalDisableCondition = false, // Added prop for additional disable condition
  allowPasteImage = false,

  // Callbacks
  onCapture = null,
  onRemoveCurrentFile = null,
  onRemoveSelectedFile = null,
  onPreviewFile = null,
  onPreviewSelectedFile = null,

  // Customization
  showCapture = true,
  showBrowse = true,
  browseLabel = "Browse",
  captureLabel = "Capture",
  browseTitle = "Choose File",
  browseDescription = "Select PDF or image file from device",
  captureTitle = "Capture with Camera",
  captureDescription = "Take photos and convert to PDF",

  // File state (for controlled mode)
  selectedFile = null,
  isCaptured = false,

  // Additional classes
  className = "",
}) => {
  const [internalFile, setInternalFile] = useState(null);
  const [internalIsCaptured, setInternalIsCaptured] = useState(false);

  // Use controlled or uncontrolled mode
  const file = selectedFile !== null ? selectedFile : internalFile;
  const capturedMode = isCaptured || internalIsCaptured;

  const hasFile = Boolean(file);
  const hasOriginalFile = Boolean(currentFileUrl);

  // Generate unique ID for file input
  const [inputId] = useState(
    `file-upload-${Math.random().toString(36).substr(2, 9)}`
  );

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setInternalFile(selectedFile);
      setInternalIsCaptured(false);
      onFileChange?.(selectedFile, false);
    }
  };

  const handleCapture = () => {
    if (onCapture) {
      onCapture((capturedFile) => {
        setInternalFile(capturedFile);
        setInternalIsCaptured(true);
        onFileChange?.(capturedFile, true);
      });
    }
  };

  const handleRemoveSelectedFile = () => {
    setInternalFile(null);
    setInternalIsCaptured(false);

    // Reset file input
    const fileInput = document.getElementById(inputId);
    if (fileInput) {
      fileInput.value = "";
    }

    if (onRemoveSelectedFile) {
      onRemoveSelectedFile();
    } else {
      onFileChange?.(null, false);
    }
  };

  const handleRemoveCurrentFile = () => {
    if (onRemoveCurrentFile) {
      onRemoveCurrentFile();
    }
  };

  const handlePreviewFile = () => {
    if (currentFileUrl && onPreviewFile) {
      onPreviewFile(currentFileUrl);
    } else if (currentFileUrl) {
      // Default behavior: open in new tab
      window.open(currentFileUrl, "_blank");
    }
  };

  const getFileDisplayName = () => {
    if (capturedMode) {
      return file?.name || (accept?.includes("pdf") ? "Captured Document" : "Captured Diagram");
    }
    return file?.name || "Unknown file";
  };

  const getFileTypeLabel = () => {
    if (capturedMode) {
      return accept?.includes("pdf") ? "Camera captured PDF" : "Camera captured image";
    }
    return "Uploaded file";
  };

  return (
    <div className={`file-upload-section ${className}`}>
      <div className="file-upload-options">
        {/* Paste Option */}
        {allowPasteImage && accept?.includes("image") && (
          <div
            className={`upload-option-group ${
              hasFile && !capturedMode ? "active" : ""
            } ${disabled || additionalDisableCondition ? "disabled" : ""}`}
            style={{
              borderColor: "#bbf7d0",
              background: "#f0fdf4",
            }}
          >
            <div className="upload-option-icon file-icon" style={{ background: "linear-gradient(135deg, #10b981 0%, #059669 100%)" }}>
              <i className="fa-solid fa-paste"></i>
            </div>
            <div className="upload-option-content">
              <div className="upload-option-title" style={{ color: "#16a34a" }}>Paste Image</div>
              <div className="upload-option-description" style={{ color: "#475569" }}>
                Paste copied image directly from clipboard
              </div>
            </div>
            <button
              type="button"
              onClick={async () => {
                try {
                  if (!navigator.clipboard || !navigator.clipboard.read) {
                    alert("Clipboard API is not supported in this browser or environment (requires HTTPS/localhost).");
                    return;
                  }
                  const clipboardItems = await navigator.clipboard.read();
                  let imageBlob = null;
                  for (const item of clipboardItems) {
                    for (const type of item.types) {
                      if (type.startsWith("image/")) {
                        imageBlob = await item.getType(type);
                        break;
                      }
                    }
                    if (imageBlob) break;
                  }
                  if (!imageBlob) {
                    alert("No image found in clipboard. Please copy an image first!");
                    return;
                  }
                  const file = new File([imageBlob], "image.png", { type: imageBlob.type });
                  onFileChange?.(file, false);
                } catch (err) {
                  console.error("Paste error:", err);
                  if (err.name === "NotAllowedError") {
                    alert("Clipboard access denied. Please allow clipboard permissions to paste images.");
                  } else {
                    alert("Failed to read image from clipboard: " + err.message);
                  }
                }
              }}
              disabled={
                isSubmitting ||
                capturedMode ||
                disabled ||
                additionalDisableCondition
              }
              className="btn-file-action"
              style={{
                background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                boxShadow: "0 2px 4px rgba(16, 185, 129, 0.2)",
              }}
              onMouseEnter={(e) => {
                if (!isSubmitting && !capturedMode && !disabled && !additionalDisableCondition) {
                  e.currentTarget.style.background = "linear-gradient(135deg, #059669 0%, #10b981 100%)";
                  e.currentTarget.style.transform = "translateY(-1px)";
                  e.currentTarget.style.boxShadow = "0 4px 8px rgba(16, 185, 129, 0.3)";
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "linear-gradient(135deg, #10b981 0%, #059669 100%)";
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "0 2px 4px rgba(16, 185, 129, 0.2)";
              }}
            >
              <i className="fa-solid fa-paste"></i>
              <span>Paste</span>
            </button>
          </div>
        )}

        {/* Browse Option */}
        {showBrowse && (
          <div
            className={`upload-option-group ${
              hasFile && !capturedMode ? "active" : ""
            } ${disabled || additionalDisableCondition ? "disabled" : ""}`}
          >
            <div className="upload-option-icon file-icon">
              <i className="fa-solid fa-file"></i>
            </div>
            <div className="upload-option-content">
              <div className="upload-option-title">{browseTitle}</div>
              <div className="upload-option-description">
                {browseDescription}
              </div>
            </div>
            <input
              type="file"
              accept={accept}
              onChange={handleFileSelect}
              disabled={
                isSubmitting ||
                capturedMode ||
                disabled ||
                additionalDisableCondition
              }
              style={{ display: "none" }}
              id={inputId}
            />
            <label
              htmlFor={inputId}
              className={`btn-file-action btn-browse ${
                isSubmitting ||
                capturedMode ||
                disabled ||
                additionalDisableCondition
                  ? "disabled"
                  : ""
              }`}
            >
              <i className="fa-solid fa-upload"></i>
              <span>{browseLabel}</span>
            </label>
          </div>
        )}

        {/* Capture Option */}
        {showCapture && onCapture && (
          <div
            className={`upload-option-group ${capturedMode ? "active" : ""} ${
              disabled || additionalDisableCondition ? "disabled" : ""
            }`}
          >
            <div className="upload-option-icon camera-icon">
              <i className="fa-solid fa-camera"></i>
            </div>
            <div className="upload-option-content">
              <div className="upload-option-title">{captureTitle}</div>
              <div className="upload-option-description">
                {captureDescription}
              </div>
            </div>
            <button
              type="button"
              onClick={handleCapture}
              disabled={
                isSubmitting ||
                (hasFile && !capturedMode) ||
                disabled ||
                additionalDisableCondition
              }
              className="btn-file-action btn-capture"
            >
              <i className="fa-solid fa-camera"></i>
              <span>{captureLabel}</span>
            </button>
          </div>
        )}
      </div>

      {/* Current File Status (for edit mode) */}
      {hasOriginalFile && (
        <div className="file-status current-file-status">
          <div className="current-file-header">
            <span className="current-file-label">Current File:</span>
          </div>
          <div className="file-status-content">
            <div className="file-status-icon current">
              <i className="fa-solid fa-file"></i>
            </div>
            <div className="file-status-text">
              <div className="file-status-name">
                {currentFileName || currentFileUrl.split("/").pop()}
              </div>
              <div className="file-status-type">Existing file</div>
            </div>
            <div className="file-action-buttons">
              <button
                type="button"
                className="btn-preview-file"
                onClick={handlePreviewFile}
                disabled={
                  isSubmitting || disabled || additionalDisableCondition
                }
                title="Preview file"
              >
                <i className="fa-solid fa-eye"></i>
              </button>
              {onRemoveCurrentFile && (
                <button
                  type="button"
                  className="btn-remove-file"
                  onClick={handleRemoveCurrentFile}
                  disabled={
                    isSubmitting || disabled || additionalDisableCondition
                  }
                  title="Remove current file"
                >
                  <i className="fa-solid fa-xmark"></i>
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Selected/Captured File Status */}
      {hasFile && (
        <div
          className={`file-status ${
            capturedMode ? "captured-file" : "uploaded-file"
          }`}
        >
          <div className="file-status-content">
            <div
              className={`file-status-icon ${
                capturedMode ? "captured" : "uploaded"
              }`}
            >
              <i
                className={`fa-solid ${capturedMode ? "fa-camera" : "fa-file"}`}
              ></i>
            </div>
            <div className="file-status-text">
              <div className="file-status-name">{getFileDisplayName()}</div>
              <div className="file-status-type">{getFileTypeLabel()}</div>
            </div>
            <div className="file-action-buttons" style={{ display: "flex", gap: "6px" }}>
              {onPreviewSelectedFile && (
                <button
                  type="button"
                  className="btn-preview-file"
                  onClick={() => onPreviewSelectedFile(file)}
                  disabled={isSubmitting || disabled || additionalDisableCondition}
                  title="Preview/Download file"
                >
                  <i className="fa-solid fa-download"></i>
                </button>
              )}
              <button
                type="button"
                onClick={handleRemoveSelectedFile}
                className="btn-remove-file"
                disabled={isSubmitting || disabled || additionalDisableCondition}
                title="Remove selected file"
              >
                <i className="fa-solid fa-xmark"></i>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FileUploadSection;